import axios, {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import React from 'react';
import store from '../store/store';
import { getRefreshToken } from 'selectors/auth/getRefreshToken';
import { fetchGoogleOauth2TokensSuccess, logout } from 'modules/auth/actions';
import { GOOGLE_REFRESH_TOKEN_ENDPOINT } from 'modules/auth/constants';
import {
  connectionStatus,
  getCurrentConnectionStatus,
} from 'utils/connectionStatus';
import toast from 'react-hot-toast';
import omit from 'lodash/omit';
import { getAccessToken } from 'selectors/auth/getAccessToken';
import { getAuthHeaders } from 'api/getAuthHeaders';
import { getRequestFormErrorMessage } from 'utils/signing/getRequestFormErrorMessage';
import {
  QueuedFormRequest,
  getRequestKey,
  isDefinitiveFailure,
  isQueued,
  readQueue,
  wasNeverSent,
  writeQueue,
} from 'api/offlineFormQueue';

export const baseURL = `${import.meta.env.VITE_API_URL}/api`;

export const api = axios.create({
  baseURL,
});

type RetriableRequestConfig = InternalAxiosRequestConfig & { _retry?: boolean };

type RefreshWaiter = {
  resolve: (accessToken: string) => void;
  reject: (error: unknown) => void;
};

let isRefreshing = false;
let refreshQueue: RefreshWaiter[] = [];
let offlineRequestsQueue: QueuedFormRequest[] = readQueue();

const Message = ({
  children,
  t,
}: {
  children: React.ReactNode;
  t: { id: string };
}) => (
  <div style={{ display: 'flex', alignItems: 'center' }}>
    <p style={{ flex: 1, margin: 0, padding: 0 }}>{children}</p>
    <button
      style={{
        background: 'white',
        border: 'none',
        borderRadius: 4,
        padding: '5px 10px',
        marginLeft: 15,
        backgroundColor: '#ededed',
      }}
      onClick={() => toast.dismiss(t.id)}>
      Dismiss
    </button>
  </div>
);

/**
 * Replays one queued request.
 *
 * Two things matter here. It goes through `api`, not bare `axios`, so the
 *   response interceptor's token refresh applies — coming back after a long
 *   spell offline is exactly when the access token has expired, and the bare
 *   instance has no interceptors at all, so every replay used to die on a 401
 *   that nothing retried.
 *
 * And the auth header is rebuilt from the store rather than replayed: the one
 *   captured at queue time is as old as the queue.
 *
 * **Only that header is replaced.** Everything else, body and remaining headers
 *   included, is reused verbatim. That is not tidiness, it is the precondition
 *   for an idempotency key: the key must be written once, where the request is
 *   built, and never by anything that runs per transmission. Routing replays
 *   through this instance is what buys the token refresh, and it also means the
 *   request interceptors now run on replays — so an interceptor that minted or
 *   attached the key would hand out a fresh one on every replay, the backend
 *   would read it as a new intention, and a second document would be created
 *   with nothing failing anywhere. `api.offlineQueue.test.tsx` holds that rule
 *   in place before there is a key to break.
 */
const replayQueuedRequest = (item: QueuedFormRequest) =>
  api({
    ...omit(item.config, 'transformRequest', 'transformResponse'),
    headers: {
      ...item.config.headers,
      ...getAuthHeaders(store.getState()),
    },
  } as InternalAxiosRequestConfig);

/**
 * Decides what happens to a single item once its replay has settled, and
 *   returns it if it should stay queued.
 *
 * Settling per item is the point: this used to be one `Promise.all` that
 *   cleared the entire queue on the first rejection, so one failing request
 *   took every other therapist's pending request with it.
 */
const settleQueuedRequest = async (
  item: QueuedFormRequest,
): Promise<{ keep: boolean; failure?: string }> => {
  try {
    await replayQueuedRequest(item);
    return { keep: false };
  } catch (e) {
    const error = e as AxiosError;

    // Aborted before anything was sent, because the connection dropped again
    //   mid-replay. Nothing reached the backend, so it stays for next time.
    if (wasNeverSent(error)) return { keep: true };

    // The backend answered on the merits. Replaying would get the same answer,
    //   so it leaves the queue and the therapist is told why.
    if (isDefinitiveFailure(error)) {
      return {
        keep: false,
        // Narrowed rather than passed raw: axios types the body as `unknown`,
        //   and the shared helper's contract is deliberately narrower.
        failure: getRequestFormErrorMessage({
          status: error.response?.status,
          data: error.response?.data as { message?: string } | string,
        }),
      };
    }

    // Unknown outcome — a dropped connection, a timeout, a 5xx. The backend may
    //   have created the document and lost the response on the way back, and
    //   there is no idempotency key yet, so replaying risks a second document
    //   for the same patient. Losing it is the lesser of the two, but it is a
    //   real loss, so it is named rather than folded into a generic toast.
    //   Revisit once `Idempotency-Key` is honoured end to end: this is the
    //   branch that becomes a retry.
    return {
      keep: false,
      failure: `${item.form_type} request could not be confirmed and was not sent. Please request it again.`,
    };
  }
};

const syncOfflineFormRequests = async () => {
  if (offlineRequestsQueue.length === 0) return;

  const toastId = toast.loading('Syncing offline form requests');
  const pending = offlineRequestsQueue;

  const outcomes = await Promise.all(pending.map(settleQueuedRequest));

  offlineRequestsQueue = pending.filter((_, index) => outcomes[index].keep);
  writeQueue(offlineRequestsQueue);

  const failures = outcomes
    .map(outcome => outcome.failure)
    .filter((failure): failure is string => Boolean(failure));

  if (failures.length === 0) {
    toast.success(
      (t: { id: string }) => <Message t={t}>Form requests were synced</Message>,
      { id: toastId, duration: 30000 },
    );
    return;
  }

  toast.error(
    (t: { id: string }) => (
      <Message t={t}>
        {failures.length === 1
          ? failures[0]
          : `${failures.length} form requests could not be sent: ${failures.join(' ')}`}
      </Message>
    ),
    { id: toastId, duration: 30000 },
  );
};

connectionStatus.on('online', function () {
  syncOfflineFormRequests();
});

/**
 * Queues a `send_form` request instead of letting it fail while offline.
 *
 * The request is aborted before it goes out and stored for replay on reconnect.
 *   Deduplication is by case *and* form type: keying on form type alone meant a
 *   therapist working through two patients offline had the second request
 *   silently dropped, because an intake was already queued for the first.
 */
const handleOfflineRequest = (config: InternalAxiosRequestConfig) => {
  if (
    getCurrentConnectionStatus() !== 'offline' ||
    !config ||
    !config.url?.includes('send_form') ||
    !config.data
  ) {
    return config;
  }

  const { form_type } = config.data;
  const key = getRequestKey(config.url, form_type);

  // Already waiting to be sent: abort without queueing a duplicate. This also
  //   covers going offline mid-replay, where the item is still in the queue.
  if (isQueued(offlineRequestsQueue, key)) {
    const controller = new AbortController();
    controller.abort();
    return { ...config, signal: controller.signal };
  }

  const controller = new AbortController();
  controller.abort();

  offlineRequestsQueue = [...offlineRequestsQueue, { key, form_type, config }];
  writeQueue(offlineRequestsQueue);

  return { ...config, signal: controller.signal };
};

api.interceptors.request.use(
  config => handleOfflineRequest(config),
  (error: AxiosError) => error.config as InternalAxiosRequestConfig,
);

/**
 * Whether this is the refresh call itself.
 *
 * It has to be excluded, and the failure it caused was not subtle. The refresh
 *   goes out through this same instance, so its own `401` — which is what the
 *   API answers for an expired refresh token, checked against a live
 *   deployment — re-entered this handler. `isRefreshing` was already `true`, so
 *   the refresh's rejection was parked on `refreshQueue` instead of being
 *   thrown; the `await` on it never settled, the `catch` never ran, the
 *   `finally` never reset `isRefreshing`, and `logout()` was never dispatched.
 *
 * The therapist was left with every authenticated request hanging for ever: no
 *   error to read, no sign-in to return to, and a reload landing in the same
 *   place because the tokens are persisted. The one case this whole block exists
 *   to handle — "the session is unrecoverable, sign out" — was the one it could
 *   not reach.
 *
 * Matched on the path rather than with `includes`, so a resource that merely
 *   contains the word cannot be mistaken for it.
 */
const isTokenRefreshRequest = ({ url }: RetriableRequestConfig): boolean => {
  const path = (url ?? '').split('?')[0].replace(/\/+$/, '');
  return (
    path === GOOGLE_REFRESH_TOKEN_ENDPOINT ||
    path.endsWith(`/${GOOGLE_REFRESH_TOKEN_ENDPOINT}`)
  );
};

const retryWithFreshToken = (
  originalRequest: RetriableRequestConfig,
  accessToken: string,
) => {
  originalRequest.headers = {
    ...originalRequest.headers,
    Authorization: `Bearer ${accessToken}`,
  } as InternalAxiosRequestConfig['headers'];
  return api(originalRequest);
};

api.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetriableRequestConfig | undefined;

    if (
      !originalRequest ||
      !error.response ||
      error.response.status !== 401 ||
      originalRequest._retry ||
      // Refreshing is what this handler does; it cannot also be what it
      //   handles. Rejecting lets the `catch` below sign the therapist out.
      isTokenRefreshRequest(originalRequest)
    ) {
      return Promise.reject(error);
    }

    const refreshToken = getRefreshToken(store.getState());
    // Dev environments may seed an access token without a refresh token; in
    // that case there is nothing we can refresh. Only force a logout when we
    // have neither token, so a real user with an expired session is signed
    // out, but a dev with a manually-set access token is left alone.
    if (!refreshToken) {
      const accessToken = getAccessToken(store.getState());
      if (!accessToken) {
        store.dispatch(logout());
      }
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    if (isRefreshing) {
      // Wait for the in-flight refresh to settle, then replay the request
      // with the freshly-issued access token. The retried call's outcome is
      // what the original caller observes.
      return new Promise((resolve, reject) => {
        refreshQueue.push({
          resolve: (newAccessToken: string) => {
            retryWithFreshToken(originalRequest, newAccessToken).then(
              resolve,
              reject,
            );
          },
          reject,
        });
      });
    }

    isRefreshing = true;
    try {
      const response = await api.post(GOOGLE_REFRESH_TOKEN_ENDPOINT, {
        refresh_token: refreshToken,
      });
      const { access_token } = response.data;
      store.dispatch(fetchGoogleOauth2TokensSuccess(response.data));

      // Drain the queue first so concurrent waiters see the new token.
      const waiters = refreshQueue;
      refreshQueue = [];
      waiters.forEach(waiter => waiter.resolve(access_token));

      // Return the retried response so the original Promise actually resolves
      // with the successful payload instead of the stale 401.
      return retryWithFreshToken(originalRequest, access_token);
    } catch (refreshError) {
      const waiters = refreshQueue;
      refreshQueue = [];
      waiters.forEach(waiter => waiter.reject(refreshError));
      // Refresh token is invalid or expired — the session is unrecoverable.
      store.dispatch(logout());
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);
